import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";

type ScrollContextValue = {
  /** Total scroll progress 0..1 across the whole page. */
  progress: number;
  /** Active section index (0..3). */
  section: number;
  /** Per-section progress 0..1 within the active section. */
  sectionProgress: number;
  /** Number of sections (each is one viewport tall). */
  sections: number;
};

const ScrollContext = createContext<ScrollContextValue>({
  progress: 0,
  section: 0,
  sectionProgress: 0,
  sections: 4,
});

export const SECTION_COUNT = 13;

export function ScrollProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ScrollContextValue>({
    progress: 0,
    section: 0,
    sectionProgress: 0,
    sections: SECTION_COUNT,
  });
  const raf = useRef<number | null>(null);

  useEffect(() => {
    const compute = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      const p = max > 0 ? Math.min(1, Math.max(0, window.scrollY / max)) : 0;
      const sectionFloat = p * (SECTION_COUNT - 1);
      const section = Math.min(SECTION_COUNT - 1, Math.floor(sectionFloat + 1e-6));
      const sectionProgress = sectionFloat - section;
      setState({ progress: p, section, sectionProgress, sections: SECTION_COUNT });
      raf.current = null;
    };
    const onScroll = () => {
      if (raf.current != null) return;
      raf.current = requestAnimationFrame(compute);
    };
    compute();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", compute);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", compute);
      if (raf.current != null) cancelAnimationFrame(raf.current);
    };
  }, []);

  const value = useMemo(() => state, [state]);
  return <ScrollContext.Provider value={value}>{children}</ScrollContext.Provider>;
}

export function useScroll() {
  return useContext(ScrollContext);
}
